'use strict'

// Step workflows start from one record and run a list of steps.
// A branch step holds grouped paths. The first matching group runs, then
// execution continues after the branch.

const { matchesDisplayCondition } = require('./display-rules')

const IDENTIFIER = /^[A-Za-z_][A-Za-z0-9_]*$/
const STEP_TYPES = ['modify_record', 'query', 'trigger_automation', 'branch', 'for_each', 'create_record', 'set_variable', 'stop']
const RESULT_MODES = ['first', 'count', 'list']
const QUERY_OPERATORS = ['=', '!=', '<', '<=', '>', '>=', 'contains', 'begins', 'ends', 'empty', 'notEmpty']
const SKIP_WRITE_TYPES = new Set(['password', 'image', 'file', 'formula', 'relationship', 'signature', 'sequence'])
const HIDDEN_VALUE_TYPES = new Set(['password', 'image', 'file', 'signature'])

const LIMITS = {
  steps: 80,
  depth: 6,
  branches: 8,
  conditions: 12,
  assignments: 30,
  filters: 12,
  variables: 20,
  relatedWrites: 100,
  startRecords: 25,
  executedSteps: 200,
  scheduleRecords: 100
}

function isIdentifier (value) {

  return typeof value === 'string' && IDENTIFIER.test(value)
}

function safeVariableName (value) {

  return isIdentifier(value)
    && value !== '__proto__'
    && value !== 'constructor'
    && value !== 'prototype'
}

function text (value, max) {

  if (value == null) return ''

  return String(value).slice(0, max)
}

function newStepId () {

  return `step_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
}

function blankCondition () {

  return {
    source: 'field',
    field: '',
    variableName: '',
    variableField: '',
    operator: '=',
    dataType: 'string',
    valueSource: 'static',
    staticValue: '',
    sourceField: '',
    compareVariable: '',
    compareVariableField: ''
  }
}

function blankAssignment () {

  return {
    field: '',
    valueSource: 'static',
    staticValue: '',
    sourceField: '',
    variableName: '',
    variableField: ''
  }
}

function blankFilter () {

  return {
    field: '',
    operator: '=',
    dataType: 'text',
    valueSource: 'static',
    staticValue: '',
    sourceField: '',
    variableName: '',
    variableField: ''
  }
}

function blankBranch (kind) {

  return {
    id: newStepId(),
    kind: kind || 'if',
    label: kind === 'else' ? 'Else' : (kind === 'elseif' ? 'Else if' : 'If'),
    match: 'all',
    conditions: kind === 'else' ? [] : [blankCondition()],
    steps: []
  }
}

function blankStep (type) {

  const step = {
    id: newStepId(),
    type: STEP_TYPES.includes(type) ? type : 'modify_record',
    title: ''
  }

  if (step.type === 'modify_record') {

    step.target = 'trigger'
    step.relatedSource = 'forward'
    step.relationshipColumn = ''
    step.sourceSpreadsheet = ''
    step.sourceRelationshipColumn = ''
    step.values = [blankAssignment()]
  }

  if (step.type === 'query') {

    step.spreadSheetUniqueTitle = ''
    step.variableName = ''
    step.resultMode = 'first'
    step.maxRows = null
    step.filterMatchMode = 'all'
    step.relatedToTrigger = false
    step.relatedSource = 'forward'
    step.relationshipColumn = ''
    step.sourceSpreadsheet = ''
    step.sourceRelationshipColumn = ''
    step.filters = [blankFilter()]
  }

  if (step.type === 'trigger_automation') {

    step.automationUniqueTitle = ''
    step.recordSource = 'trigger'
    step.recordVariable = ''
    step.variables = []
  }

  if (step.type === 'branch') {

    step.branches = [blankBranch('if'), blankBranch('else')]
  }

  if (step.type === 'for_each') {

    step.listVariable = ''
    step.itemVariable = 'item'
    step.maxItems = null
    step.steps = []
  }

  if (step.type === 'create_record') {

    step.spreadSheetUniqueTitle = ''
    step.variableName = ''
    step.linkToTrigger = false
    step.relatedSource = 'forward'
    step.relationshipColumn = ''
    step.sourceSpreadsheet = ''
    step.sourceRelationshipColumn = ''
    step.values = [blankAssignment()]
  }

  if (step.type === 'set_variable') {

    step.variableName = ''
    step.valueSource = 'static'
    step.staticValue = ''
    step.sourceField = ''
    step.sourceVariable = ''
    step.sourceVariableField = ''
  }

  if (step.type === 'stop') {

    step.message = ''
  }

  return step
}

function cleanOptionalMax (value) {

  if (value === '' || value == null) return null

  const number = Number(value)

  if (!Number.isInteger(number) || number < 1) return null

  return number
}

function cleanValueSource (entry) {

  const source = entry && entry.valueSource

  if (source === 'field' || source === 'variable' || source === 'static') return source

  return 'static'
}

function cleanAssignment (entry) {

  if (!entry || typeof entry !== 'object') return null

  const valueSource = cleanValueSource(entry)

  return {
    field: isIdentifier(entry.field) ? entry.field : '',
    valueSource,
    staticValue: text(entry.staticValue, 2000),
    sourceField: isIdentifier(entry.sourceField) ? entry.sourceField : '',
    variableName: safeVariableName(entry.variableName) ? entry.variableName : '',
    variableField: isIdentifier(entry.variableField) ? entry.variableField : ''
  }
}

function cleanFilter (entry) {

  if (!entry || typeof entry !== 'object') return null

  const operator = QUERY_OPERATORS.includes(entry.operator) ? entry.operator : '='

  return {
    field: isIdentifier(entry.field) ? entry.field : '',
    operator,
    dataType: entry.dataType === 'number' ? 'number' : 'text',
    valueSource: cleanValueSource(entry),
    staticValue: text(entry.staticValue, 2000),
    sourceField: isIdentifier(entry.sourceField) ? entry.sourceField : '',
    variableName: safeVariableName(entry.variableName) ? entry.variableName : '',
    variableField: isIdentifier(entry.variableField) ? entry.variableField : ''
  }
}

function cleanCondition (entry) {

  if (!entry || typeof entry !== 'object') return null

  const source = entry.source === 'variable' ? 'variable' : 'field'

  return {
    source,
    field: isIdentifier(entry.field) ? entry.field : '',
    variableName: safeVariableName(entry.variableName) ? entry.variableName : '',
    variableField: isIdentifier(entry.variableField) ? entry.variableField : '',
    operator: text(entry.operator || '=', 40),
    dataType: text(entry.dataType || 'string', 40),
    valueSource: cleanValueSource(entry),
    staticValue: entry.staticValue != null ? text(entry.staticValue, 2000) : text(entry.value, 2000),
    sourceField: isIdentifier(entry.sourceField) ? entry.sourceField : '',
    compareVariable: safeVariableName(entry.compareVariable) ? entry.compareVariable : '',
    compareVariableField: isIdentifier(entry.compareVariableField) ? entry.compareVariableField : ''
  }
}

function cleanRelationship (step) {

  return {
    relatedSource: step && step.relatedSource === 'reverse' ? 'reverse' : 'forward',
    relationshipColumn: isIdentifier(step && step.relationshipColumn) ? step.relationshipColumn : '',
    sourceSpreadsheet: isIdentifier(step && step.sourceSpreadsheet) ? step.sourceSpreadsheet : '',
    sourceRelationshipColumn: isIdentifier(step && step.sourceRelationshipColumn) ? step.sourceRelationshipColumn : ''
  }
}

function normalizeWorkflowSteps (steps, depth, state) {

  const list = Array.isArray(steps) ? steps : []
  const level = depth || 0
  const counter = state || { count: 0 }
  const cleaned = []

  if (level > LIMITS.depth) return cleaned

  for (const step of list) {

    if (counter.count >= LIMITS.steps) break
    if (!step || typeof step !== 'object' || !STEP_TYPES.includes(step.type)) continue

    counter.count += 1

    const next = {
      id: text(step.id, 80) || `step_${counter.count}`,
      type: step.type,
      title: text(step.title, 200)
    }

    if (step.type === 'modify_record') {

      next.target = step.target === 'related' || step.target === 'variable' ? step.target : 'trigger'
      next.recordVariable = safeVariableName(step.recordVariable) ? step.recordVariable : ''
      Object.assign(next, cleanRelationship(step))
      next.values = (Array.isArray(step.values) ? step.values : []).map(cleanAssignment).filter(Boolean).slice(0, LIMITS.assignments)
    }

    if (step.type === 'query') {

      next.spreadSheetUniqueTitle = isIdentifier(step.spreadSheetUniqueTitle) ? step.spreadSheetUniqueTitle : ''
      next.variableName = safeVariableName(step.variableName) ? step.variableName : ''
      next.resultMode = RESULT_MODES.includes(step.resultMode) ? step.resultMode : 'first'
      next.maxRows = cleanOptionalMax(step.maxRows)
      next.filterMatchMode = step.filterMatchMode === 'any' ? 'any' : 'all'
      next.relatedToTrigger = step.relatedToTrigger === true
      Object.assign(next, cleanRelationship(step))
      next.filters = (Array.isArray(step.filters) ? step.filters : []).map(cleanFilter).filter(Boolean).slice(0, LIMITS.filters)
    }

    if (step.type === 'trigger_automation') {

      next.automationUniqueTitle = isIdentifier(step.automationUniqueTitle) ? step.automationUniqueTitle : ''
      next.recordSource = step.recordSource === 'variable' ? 'variable' : 'trigger'
      next.recordVariable = safeVariableName(step.recordVariable) ? step.recordVariable : ''
      next.variables = (Array.isArray(step.variables) ? step.variables : []).slice(0, LIMITS.variables).map((entry) => {

        if (!entry || !safeVariableName(entry.name)) return null

        return {
          name: entry.name,
          valueSource: cleanValueSource(entry),
          staticValue: text(entry.staticValue, 2000),
          sourceField: isIdentifier(entry.sourceField) ? entry.sourceField : '',
          variableName: safeVariableName(entry.variableName) ? entry.variableName : '',
          variableField: isIdentifier(entry.variableField) ? entry.variableField : ''
        }
      }).filter(Boolean)
    }

    if (step.type === 'branch') {

      const branches = []
      let sawElse = false

      for (const branch of (Array.isArray(step.branches) ? step.branches : [])) {

        if (!branch || typeof branch !== 'object' || branches.length >= LIMITS.branches) continue

        const kind = branch.kind === 'else' || branch.kind === 'elseif' ? branch.kind : 'if'

        if (kind === 'else') {

          if (sawElse) continue
          sawElse = true
        }

        branches.push({
          id: text(branch.id, 80) || newStepId(),
          kind,
          label: text(branch.label, 200),
          match: branch.match === 'any' ? 'any' : 'all',
          conditions: kind === 'else'
            ? []
            : (Array.isArray(branch.conditions) ? branch.conditions : []).map(cleanCondition).filter(Boolean).slice(0, LIMITS.conditions),
          steps: normalizeWorkflowSteps(branch.steps, level + 1, counter)
        })
      }

      next.branches = branches
    }

    if (step.type === 'for_each') {

      next.listVariable = safeVariableName(step.listVariable) ? step.listVariable : ''
      next.itemVariable = safeVariableName(step.itemVariable) ? step.itemVariable : 'item'
      next.maxItems = cleanOptionalMax(step.maxItems)
      next.steps = normalizeWorkflowSteps(step.steps, level + 1, counter)
    }

    if (step.type === 'create_record') {

      next.spreadSheetUniqueTitle = isIdentifier(step.spreadSheetUniqueTitle) ? step.spreadSheetUniqueTitle : ''
      next.variableName = safeVariableName(step.variableName) ? step.variableName : ''
      next.linkToTrigger = step.linkToTrigger === true
      Object.assign(next, cleanRelationship(step))
      next.values = (Array.isArray(step.values) ? step.values : []).map(cleanAssignment).filter(Boolean).slice(0, LIMITS.assignments)
    }

    if (step.type === 'set_variable') {

      next.variableName = safeVariableName(step.variableName) ? step.variableName : ''
      next.valueSource = cleanValueSource(step)
      next.staticValue = text(step.staticValue, 2000)
      next.sourceField = isIdentifier(step.sourceField) ? step.sourceField : ''
      next.sourceVariable = safeVariableName(step.sourceVariable) ? step.sourceVariable : ''
      next.sourceVariableField = isIdentifier(step.sourceVariableField) ? step.sourceVariableField : ''
    }

    if (step.type === 'stop') {

      next.message = text(step.message, 500)
    }

    cleaned.push(next)
  }

  return cleaned
}

function collectVariableNames (steps, found) {

  const names = found || []

  for (const step of steps || []) {

    if (step && (step.type === 'query' || step.type === 'create_record' || step.type === 'set_variable') && step.variableName && names.indexOf(step.variableName) === -1) {

      names.push(step.variableName)
    }

    if (step && step.type === 'for_each') {

      if (step.itemVariable && names.indexOf(step.itemVariable) === -1) names.push(step.itemVariable)
      collectVariableNames(step.steps, names)
    }

    if (step && step.type === 'branch') {

      for (const branch of step.branches || []) collectVariableNames(branch.steps, names)
    }
  }

  return names
}

function readVariable (variables, name, field) {

  if (!variables || !safeVariableName(name)) return undefined

  const value = variables[name]

  if (field && value && typeof value === 'object' && !Array.isArray(value)) return value[field]

  return value
}

function resolveValue (source, context) {

  const record = context && context.record
  const variables = context && context.variables

  if (!source) return undefined

  if (source.valueSource === 'field') {

    if (!isIdentifier(source.sourceField) || !record) return undefined

    return record[source.sourceField]
  }

  if (source.valueSource === 'variable') {

    return readVariable(variables, source.variableName, source.variableField)
  }

  return source.staticValue
}

function conditionLeft (condition, context) {

  if (!condition) return undefined

  if (condition.source === 'variable') {

    return readVariable(context && context.variables, condition.variableName, condition.variableField)
  }

  const record = context && context.record

  if (!record || !isIdentifier(condition.field)) return undefined

  return record[condition.field]
}

function conditionRight (condition, context) {

  if (!condition) return undefined
  if (condition.valueSource === 'field') return resolveValue({ valueSource: 'field', sourceField: condition.sourceField }, context)
  if (condition.valueSource === 'variable') return readVariable(context && context.variables, condition.compareVariable, condition.compareVariableField)

  if (condition.staticValue !== undefined && condition.staticValue !== '') return condition.staticValue

  return condition.value
}

function conditionMatches (condition, context) {

  if (!condition) return false
  if (condition.source === 'variable' && !safeVariableName(condition.variableName)) return false
  if (condition.source !== 'variable' && !isIdentifier(condition.field)) return false

  return matchesDisplayCondition(
    conditionLeft(condition, context),
    condition.operator || '=',
    conditionRight(condition, context),
    condition.dataType || 'string'
  )
}

function matchingBranch (step, context) {

  const branches = (step && step.branches) || []
  let elseBranch = null

  for (const branch of branches) {

    if (!branch) continue
    if (branch.kind === 'else') {

      elseBranch = branch
      continue
    }

    const conditions = branch.conditions || []

    if (!conditions.length) return branch

    const results = conditions.map((condition) => conditionMatches(condition, context))
    const matched = branch.match === 'any' ? results.some(Boolean) : results.every(Boolean)

    if (matched) return branch
  }

  return elseBranch
}

function queryFilters (step, context) {

  return (step && step.filters || []).filter((filter) => isIdentifier(filter.field)).map((filter) => {

    const item = {
      source: 'direct',
      field: filter.field,
      operator: filter.operator || '=',
      dataType: filter.dataType === 'number' ? 'number' : 'text'
    }

    if (item.operator !== 'empty' && item.operator !== 'notEmpty') {

      item.value = resolveValue(filter, context)
    }

    return item
  })
}

function assignmentValues (assignments, context, columns) {

  const allowed = new Map()

  for (const column of columns || []) {

    if (column && isIdentifier(column.uniqueTitle) && !SKIP_WRITE_TYPES.has(column.type)) {

      allowed.set(column.uniqueTitle, column)
    }
  }

  const values = {}

  for (const assignment of assignments || []) {

    if (!assignment || !allowed.has(assignment.field)) continue

    const value = resolveValue(assignment, context)

    if (value === undefined) continue

    values[assignment.field] = value
  }

  return values
}

function hiddenColumnNames (columns) {

  const names = new Set()

  for (const column of columns || []) {

    if (!column || !HIDDEN_VALUE_TYPES.has(column.type) || !column.uniqueTitle) continue

    names.add(column.uniqueTitle)
    names.add(`${column.uniqueTitle}_type`)
    names.add(`${column.uniqueTitle}_filename`)
  }

  return names
}

function normalizeWorkflowTrigger (action) {

  if (!action || action.type !== 'run_step_workflow') return null
  if (!isIdentifier(action.workflowUniqueTitle)) return null

  const next = {
    type: 'run_step_workflow',
    workflowUniqueTitle: action.workflowUniqueTitle,
    recordSource: action.recordSource === 'related' ? 'related' : 'form'
  }

  if (next.recordSource !== 'related') return next

  next.relatedSource = action.relatedSource === 'reverse' ? 'reverse' : 'forward'

  if (next.relatedSource === 'reverse') {

    if (!isIdentifier(action.sourceSpreadsheet) || !isIdentifier(action.sourceRelationshipColumn)) return null

    next.sourceSpreadsheet = action.sourceSpreadsheet
    next.sourceRelationshipColumn = action.sourceRelationshipColumn

    return next
  }

  if (!isIdentifier(action.relationshipColumn)) return null

  next.relationshipColumn = action.relationshipColumn

  return next
}

module.exports = {
  IDENTIFIER,
  STEP_TYPES,
  LIMITS,
  QUERY_OPERATORS,
  SKIP_WRITE_TYPES,
  isIdentifier,
  safeVariableName,
  blankCondition,
  blankAssignment,
  blankFilter,
  blankBranch,
  blankStep,
  normalizeWorkflowSteps,
  collectVariableNames,
  cleanOptionalMax,
  resolveValue,
  conditionMatches,
  matchingBranch,
  queryFilters,
  assignmentValues,
  hiddenColumnNames,
  normalizeWorkflowTrigger
}
